import { FormButtonsLayout } from 'framework/ui/layout';
import { useState } from 'react';
import { UpdateForm } from 'framework/form';
import FlagAsInappropriateRadioButtonGroup, {
  FEEDBACK_FLAGS,
} from 'domain/people/employee/Timeline/feedback/shared/input/FlagAsInappropriateRadioButtonGroup';
import useEmployeeFeedbackService from 'domain/people/employee/Timeline/feedback/employeeFeedbackService';
import { useListContext } from 'framework/list';
import { SmallLoader } from 'framework/ui/mui-builder';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import type { Data } from 'framework/types';
import { TimelineContextData } from 'framework/ui/timeline';
import { CANCEL_INAPPROPRIATE_FLAGGING_LABEL, INAPPROPRIATE_FLAGGING_LABEL } from 'domain/shared/timeline';
import { dropdownMenuPaperSx } from 'framework/ui/styles';

export const FEEDBACK_INAPPROPRIATE_DEFAULT_FLAG = 'NONE';

const FeedbackFlagAsInappropriateButton = ({ entity }: { entity: Data }) => {
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const flag = entity.getValueBySource('inappropriateContentType');
  const displayedFlag = FEEDBACK_FLAGS[flag];

  const { updateFlag, getFlagValidationSchema } = useEmployeeFeedbackService();
  const { updateInList, wrapItem }: TimelineContextData = useListContext();
  const { addMessage } = useMessageService();

  const updateRecordInList = (data: any) => {
    if (updateInList && wrapItem) {
      updateInList(data.timelineId, wrapItem(data, 'feedback'));
    }
  };

  const toggleDropdownState = async () => {
    if (displayedFlag && !isDropdownOpen) {
      try {
        setIsSubmitting(true);
        const response = await updateFlag(entity.getValueBySource('id'), {
          ...entity.getData(),
          inappropriateContentType: FEEDBACK_INAPPROPRIATE_DEFAULT_FLAG,
        });
        updateRecordInList(response);
      } catch {
        addMessage(new ErrorMessage('Sorry, feedback cannot be flagged'));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setIsDropdownOpen(prev => !prev);
    }
  };

  if (isSubmitting && displayedFlag) {
    return <SmallLoader />;
  }

  return (
    <>
      <MenuItem
        onClick={e => {
          setAnchorEl(e.currentTarget);
          toggleDropdownState();
        }}
        sx={{
          width: '100%',
          '.actions-dropdown-menu.is-hidden &': { position: 'static !important' },
        }}
      >
        {displayedFlag ? CANCEL_INAPPROPRIATE_FLAGGING_LABEL : INAPPROPRIATE_FLAGGING_LABEL}
      </MenuItem>
      <Popper
        open={isDropdownOpen}
        anchorEl={anchorEl}
        placement="bottom-end"
        transition
        disablePortal
        sx={{ zIndex: 1200, transform: 'none !important' }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper
              elevation={3}
              sx={{
                ...dropdownMenuPaperSx,
                position: 'absolute',
                minWidth: '250px',
                visibility: 'visible',
                transform: 'none !important',
                top: '-3px !important',
                right: '-1px !important',
                left: 'auto !important',
                cursor: 'default',
                paddingTop: '5px',
                '& .MuiFormGroup-root': { margin: '3px 0 10px' },
                '& .MuiList-root': {
                  paddingRight: '16px',
                  paddingLeft: '16px',
                  paddingBottom: '16px',
                  '& .Mui-checked .MuiSvgIcon-root': { fill: 'primary.main' },
                  '& .MuiSvgIcon-root': { width: '20px', height: '20px', margin: 0 },
                },
              }}
            >
              <ClickAwayListener
                onClickAway={() => {
                  setAnchorEl(null);
                }}
              >
                <MenuList autoFocusItem={false}>
                  <UpdateForm
                    getDataFunc={() => Promise.resolve({ inappropriateContentType: flag })}
                    getValidationSchemaFunc={getFlagValidationSchema}
                    submitFormFunc={(id, data) => updateFlag(entity.getValueBySource('id'), data)}
                    afterSubmit={{
                      execute: (id, data) => {
                        updateRecordInList(data);
                      },
                    }}
                  >
                    <div onClick={e => e.stopPropagation()} role="button" tabIndex={0} onKeyPress={() => {}}>
                      <FlagAsInappropriateRadioButtonGroup
                        source="inappropriateContentType"
                        label={INAPPROPRIATE_FLAGGING_LABEL}
                      />
                    </div>
                    <FormButtonsLayout onCancelAction={toggleDropdownState} />
                  </UpdateForm>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default FeedbackFlagAsInappropriateButton;
